import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ChevronLeft, ChevronRight, Maximize2, Minimize2, ArrowLeft,
  Heart, Users, Globe, HeartCrack, HeartPulse, Sprout, X, Quote,
  Smile, Ear, Handshake, Lightbulb, Sun, Compass, MessageCircle,
  EyeOff, UserMinus, Smartphone, Flame, Infinity as InfinityIcon,
  Bird, User, Sparkles, Coffee, Star,
} from "lucide-react";
import slides from "../data/herzbewegungSlides";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Live Herz-Acts pill — pulses gently, polls /api/herz/stats */
function LiveActsPill() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/herz/stats`);
        if (mounted) setStats(data);
      } catch { /* silently ignore */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, []);
  if (!stats) return null;
  const acts = stats.acts_today ?? 0;
  const total = stats.total_acts ?? 0;
  return (
    <div
      className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] tracking-[0.25em] uppercase font-semibold"
      style={{ background: "rgba(212,175,55,0.14)", border: "1px solid #D4AF3755", color: "#E8C566" }}
      data-testid="herzbewegung-live-pill"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full rounded-full opacity-70 animate-ping" style={{ background: "#D4AF37" }} />
        <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "#D4AF37" }} />
      </span>
      {acts > 0 ? `${acts} Herzakte heute` : `${total} Herzakte weltweit`}
    </div>
  );
}

/* HERZBEWEGUNG palette — Teal-Dark-Green + Gold + Cream */
const M = {
  teal: "#0F4F4F",           // primary deep teal
  tealDeep: "#0A3838",       // outer darkest
  tealMid: "#1B6B6B",        // mid teal for icons
  tealSoft: "#2A8585",       // brighter teal accent
  green: "#5A9B7E",          // accent green
  greenDeep: "#3F7861",
  gold: "#D4AF37",           // SAT gold
  goldSoft: "#E8C566",
  cream: "#F5EBE0",          // page background
  creamSoft: "#FBF4EA",      // card/inner background
  white: "#FFFFFF",
  ink: "#0F3D3E",            // primary text
  inkSoft: "#3A5658",        // secondary text
  whisper: "#7A8A8C",
};

/* Compass-style infinity heart (gold + teal gradient) */
const InfinityHeart = ({ size = 56 }) => (
  <svg viewBox="0 0 100 60" width={size} height={size * 0.6} aria-hidden>
    <defs>
      <linearGradient id="hbig" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor={M.tealSoft} />
        <stop offset="50%" stopColor={M.gold} />
        <stop offset="100%" stopColor={M.tealSoft} />
      </linearGradient>
    </defs>
    <path
      d="M25 10 C 10 10 5 30 20 40 C 35 50 50 30 50 30 C 50 30 65 50 80 40 C 95 30 90 10 75 10 C 65 10 55 18 50 28 C 45 18 35 10 25 10 Z"
      fill="none"
      stroke="url(#hbig)"
      strokeWidth="4"
      strokeLinejoin="round"
    />
  </svg>
);

const GoldDivider = () => (
  <div className="flex items-center justify-center gap-1.5 my-2">
    <span className="block w-12 h-px" style={{ background: `linear-gradient(90deg, transparent, ${M.gold})` }} />
    <Heart size={9} style={{ color: M.gold, fill: M.gold }} />
    <span className="block w-12 h-px" style={{ background: `linear-gradient(90deg, ${M.gold}, transparent)` }} />
  </div>
);

/* Icon map */
const ICONS = {
  heart: Heart, people: Users, globe: Globe, "heart-broken": HeartCrack,
  "heart-pulse": HeartPulse, plant: Sprout, "people-heart": Users,
  smile: Smile, ear: Ear, handshake: Handshake, "hand-heart": Heart,
  "hands-heart": Heart, "bulb-person": Lightbulb, sun: Sun, speech: MessageCircle,
  compass: Compass, "eye-off": EyeOff, "person-off": UserMinus, phone: Smartphone,
  candle: Flame, infinity: InfinityIcon, "globe-heart": Globe, "heart-person": Heart,
  "sparkle-heart": Sparkles, person: User, bird: Bird, cup: Coffee, star: Star,
};
const I = (key, props) => {
  const Cmp = ICONS[key] || Heart;
  return <Cmp {...props} />;
};
const colorFor = (variant) => {
  if (variant === "gold") return M.gold;
  if (variant === "green") return M.green;
  if (variant === "violet") return "#7C4FBA";
  if (variant === "rose") return "#C77B7B";
  return M.tealMid;
};

/* Multi-color title: split by words, highlight `titleAccent` segment */
const MultiTitle = ({ text, accent }) => {
  if (!accent || !text.includes(accent)) {
    return <span style={{ color: M.ink }}>{text}</span>;
  }
  const before = text.split(accent)[0];
  const after = text.split(accent)[1] || "";
  // Color accent segment with gradient across chars
  const accentColors = [M.green, M.tealMid, M.gold, M.tealMid, M.greenDeep, M.tealSoft, M.gold];
  return (
    <>
      <span style={{ color: M.ink }}>{before}</span>
      <span>
        {Array.from(accent).map((ch, i) => (
          <span key={i} style={{ color: accentColors[i % accentColors.length] }}>{ch}</span>
        ))}
      </span>
      <span style={{ color: M.ink }}>{after}</span>
    </>
  );
};

/* ── COVER (Slide 1) ───────────────────────────────────────────────────── */
const Cover = ({ s }) => (
  <div className="w-full px-8 lg:px-12 pt-7 pb-8" style={{ color: M.ink }}>
    <div className="text-[10px] tracking-[0.4em] uppercase font-semibold text-center" style={{ color: M.ink }}>
      HERZ. VERSTAND. SEELE. KRAFT. ALLE.
    </div>
    <GoldDivider />

    <div className="text-center mb-2">
      <h1 className="serif font-light leading-[1] tracking-tight" style={{ fontSize: "clamp(2.2rem,5.5vw,4.5rem)" }}>
        <MultiTitle text={s.title} accent="HERZBEWEGUNG" />
        <sup className="text-xl align-super ml-1" style={{ color: M.gold }}>™</sup>
      </h1>
      <p className="serif italic mt-2" style={{ color: M.tealMid, fontSize: "clamp(0.95rem,1.5vw,1.3rem)" }}>
        Eine Bewegung für mehr <span style={{ color: M.green }}>Menschlichkeit</span>,{" "}
        <span style={{ color: M.tealSoft }}>Mitgefühl</span> und{" "}
        <span style={{ color: M.tealMid }}>Verantwortung</span>.
      </p>
    </div>
    <GoldDivider />

    <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 mt-3 items-start">
      {/* LEFT */}
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <Heart size={14} style={{ color: M.gold, fill: M.gold }} />
          <h3 className="font-bold tracking-wide text-sm" style={{ color: M.tealMid }}>{s.sectionTitle}</h3>
        </div>
        <p className="text-sm leading-relaxed mb-3" style={{ color: M.inkSoft }}>{s.sectionLead}</p>

        <ul className="space-y-1.5 mb-3">
          {s.actions.map((a, i) => (
            <li key={i} className="flex items-center gap-2 text-sm" style={{ color: M.ink }}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: M.tealMid }}>
                <Heart size={11} style={{ color: M.white, fill: M.white }} />
              </div>
              {a}
            </li>
          ))}
        </ul>
        <p className="font-bold text-base" style={{ color: M.tealMid }}>{s.actionsClosing}</p>
      </div>

      {/* RIGHT */}
      <div className="space-y-3">
        {s.rightPanels.map((p, i) => (
          <div key={i} className="rounded-2xl p-4 border" style={{ background: M.white, borderColor: `${M.tealMid}33` }}>
            <div className="text-[11px] tracking-[0.28em] font-bold uppercase mb-2.5" style={{ color: M.ink }}>
              {p.title}
            </div>
            {p.items && (
              <div className="space-y-1.5">
                {p.items.map((it, j) => (
                  <div key={j} className="flex items-center gap-2 text-sm" style={{ color: M.ink }}>
                    <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: M.tealMid }}>
                      <Heart size={10} style={{ color: M.white, fill: M.white }} />
                    </div>
                    <span>{it}</span>
                  </div>
                ))}
              </div>
            )}
            {p.crossItems && (
              <div className="space-y-1.5">
                {p.crossItems.map((it, j) => (
                  <div key={j} className="flex items-center gap-2 text-sm" style={{ color: M.inkSoft }}>
                    <span className="shrink-0 w-5 h-5 rounded-full border flex items-center justify-center" style={{ borderColor: M.gold }}>
                      <X size={9} style={{ color: M.gold }} />
                    </span>
                    {it}
                  </div>
                ))}
                {p.closing && (
                  <div className="pt-2 text-sm" style={{ color: M.ink }}>
                    {p.closing} <span className="font-bold" style={{ color: M.tealMid }}>{p.closingBold}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        <div className="rounded-2xl p-4 border text-center" style={{ background: M.teal, color: M.cream, borderColor: M.gold }}>
          <Heart size={14} className="mx-auto mb-1" style={{ color: M.gold, fill: M.gold }} />
          <div className="serif italic text-base" style={{ color: M.goldSoft }}>{s.invitation.headline}</div>
          <div className="text-xs mt-1" style={{ color: "rgba(245,235,224,0.85)" }}>{s.invitation.sub}</div>
        </div>
      </div>
    </div>

    {/* Bottom Banner */}
    <div className="mt-5 rounded-2xl px-5 py-4 relative overflow-hidden" style={{
      background: `linear-gradient(90deg, ${M.tealDeep}, ${M.teal}, ${M.greenDeep})`,
      color: M.cream,
    }}>
      <div className="flex items-center gap-4">
        <InfinityHeart size={36} />
        <div className="flex-1 text-center">
          <div className="serif italic" style={{ color: M.goldSoft, fontSize: "clamp(1.2rem,1.6vw,1.7rem)" }}>
            {s.bottomBanner.script}
            <Heart size={14} className="inline-block ml-1 align-middle" style={{ color: M.gold, fill: M.gold }} />
          </div>
          <div className="text-sm md:text-base font-bold tracking-wider mt-1" style={{ color: M.cream }}>
            {s.bottomBanner.lineA}
          </div>
        </div>
      </div>
      <div className="mt-2 pt-2 text-center text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: M.gold, borderTop: `1px solid ${M.gold}33` }}>
        {s.bottomBanner.sub}
      </div>
    </div>
  </div>
);

/* ── CONTENT (Slides 2–6) ──────────────────────────────────────────────── */
const Content = ({ s }) => (
  <div className="w-full px-8 lg:px-12 pt-6 pb-6" style={{ color: M.ink }}>
    <div className="text-[10px] tracking-[0.4em] uppercase font-semibold text-center" style={{ color: M.ink }}>
      HERZ. VERSTAND. SEELE. KRAFT. ALLE.
    </div>
    <GoldDivider />

    <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6 mt-2 items-start">
      {/* LEFT */}
      <div>
        <h2 className="serif font-light leading-[1.05] mb-2" style={{ fontSize: "clamp(1.6rem,3.4vw,2.7rem)" }}>
          <MultiTitle text={s.title} accent={s.titleAccent} />
        </h2>
        {s.lead && (
          <p
            className={`mt-1 mb-3 font-semibold ${s.leadVariant === "gold-italic" ? "serif italic" : ""}`}
            style={{ color: s.leadVariant === "gold-italic" ? M.gold : M.tealMid, fontSize: "clamp(0.95rem,1.4vw,1.2rem)" }}
          >
            {s.lead}
          </p>
        )}

        {/* Paragraphs */}
        {s.paragraphs && (
          <div className="space-y-1.5 mb-3 max-w-xl">
            {s.paragraphs.map((p, i) => (
              <p key={i} className="text-sm leading-relaxed" style={{ color: M.inkSoft }}>{p}</p>
            ))}
          </div>
        )}

        {/* Conclusion (slide 2) */}
        {s.conclusion && (
          <p className="text-sm font-semibold mb-3" style={{ color: M.tealMid }}>
            {s.conclusion.plain} <span style={{ color: M.gold }}>{s.conclusion.accent}</span>
          </p>
        )}

        {/* Accent paragraph (slides 7, 9) — colored emphasis */}
        {s.accentParagraph && (
          <p className="text-sm font-semibold mb-3 leading-relaxed" style={{ color: M.tealMid }}>
            {s.accentParagraph}
          </p>
        )}

        {/* Reasons list (slides 3,4,5,6) */}
        {s.reasons && (
          <div className="space-y-2.5 mt-2">
            {s.reasons.map((r, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: M.tealMid }}>
                  {I(r.icon, { size: 18, style: { color: M.white } })}
                </div>
                <div>
                  <div className="font-bold text-sm tracking-wide" style={{ color: M.tealMid }}>
                    {r.kicker}{" "}
                    {r.suffix && <span style={{ color: M.ink, fontWeight: 600 }}>{r.suffix}</span>}
                  </div>
                  {r.text && (
                    <div className="text-sm leading-snug" style={{ color: M.inkSoft }}>{r.text}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Card grid (slides 2,3,4,6,7) */}
        {s.cards && (
          <div className="mt-4 rounded-2xl border p-4" style={{ background: M.white, borderColor: `${M.tealMid}33` }}>
            {s.cardsTitle && (
              <div className="text-center text-[11px] tracking-[0.28em] font-bold uppercase mb-3" style={{ color: M.ink }}>
                {s.cardsTitle}
              </div>
            )}
            <div className="grid grid-cols-[1fr_auto] gap-4 items-center">
              <div className={`grid gap-3 grid-cols-2 md:grid-cols-${Math.min(s.cards.length, 5)} lg:grid-cols-${Math.min(s.cards.length, 7)}`}>
                {s.cards.map((c, i) => {
                  if (c.cursive) {
                    return (
                      <div key={i} className="text-center flex flex-col justify-center">
                        <div className="serif italic" style={{ color: M.tealMid, fontSize: "clamp(1.1rem,1.4vw,1.4rem)" }}>{c.text}</div>
                      </div>
                    );
                  }
                  const col = colorFor(c.color);
                  return (
                    <div key={i} className="text-center">
                      <div className="mx-auto w-11 h-11 rounded-full flex items-center justify-center mb-1.5" style={{ background: col }}>
                        {I(c.icon, { size: 18, style: { color: M.white } })}
                      </div>
                      {c.h && (
                        <div className="font-bold text-[11px] tracking-wide" style={{ color: col }}>{c.h}</div>
                      )}
                      {c.t && (
                        <div className="text-[10.5px] leading-snug mt-0.5" style={{ color: M.inkSoft }}>{c.t}</div>
                      )}
                    </div>
                  );
                })}
              </div>
              {s.cardsAside && (
                <div className="text-center pl-3" style={{ borderLeft: `1px solid ${M.gold}55`, minWidth: "120px" }}>
                  <Heart size={14} className="mx-auto mb-1.5" style={{ color: M.gold, fill: M.gold }} />
                  <div className="serif italic text-sm" style={{ color: M.tealMid }}>{s.cardsAside.cursive}</div>
                  {s.cardsAside.cursive2 && (
                    <div className="serif italic text-sm" style={{ color: M.tealMid }}>{s.cardsAside.cursive2}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Center cursive mid-line (slide 8) */}
        {s.centerCursive && (
          <div className="my-3 text-center">
            <Heart size={12} className="mx-auto mb-1" style={{ color: M.gold, fill: M.gold }} />
            <div className="serif italic" style={{ color: M.tealMid, fontSize: "clamp(1.1rem,1.5vw,1.5rem)" }}>
              {s.centerCursive.line1}
            </div>
            <div className="serif italic" style={{ color: M.tealMid, fontSize: "clamp(1.1rem,1.5vw,1.5rem)" }}>
              {s.centerCursive.line2}
            </div>
            <div className="serif italic" style={{ color: M.tealMid, fontSize: "clamp(1.1rem,1.5vw,1.5rem)" }}>
              {s.centerCursive.line3}
            </div>
          </div>
        )}

        {/* Process steps (slides 8, 9) */}
        {s.processSteps && (
          <div className="mt-4 rounded-2xl border p-3" style={{ background: M.white, borderColor: `${M.tealMid}33` }}>
            {s.processTitle && (
              <div className="text-center text-[11px] tracking-[0.22em] font-bold uppercase mb-3" style={{ color: M.ink }}>
                {s.processTitle}
              </div>
            )}
            <div className="grid grid-cols-[1fr_auto] gap-3 items-start">
              <div className={`grid grid-cols-${s.processSteps.length} gap-1 items-start`}>
                {s.processSteps.map((c, i) => (
                  <React.Fragment key={i}>
                    <div className="text-center relative">
                      <div className="mx-auto w-11 h-11 rounded-full flex items-center justify-center mb-1.5" style={{ background: M.tealMid }}>
                        {I(c.icon, { size: 18, style: { color: M.white } })}
                      </div>
                      <div className="font-bold text-[9.5px] tracking-wide leading-snug uppercase" style={{ color: M.tealMid }}>{c.h}</div>
                      <div className="font-bold text-[9.5px] tracking-wide leading-snug uppercase" style={{ color: M.tealMid }}>{c.h2}</div>
                      {i < s.processSteps.length - 1 && (
                        <div className="absolute top-4 -right-1 text-lg" style={{ color: M.gold }}>›</div>
                      )}
                    </div>
                  </React.Fragment>
                ))}
              </div>
              {s.processCursive && (
                <div className="pl-2 text-center" style={{ borderLeft: `1px solid ${M.gold}55`, minWidth: "150px" }}>
                  <Heart size={12} className="mx-auto mb-1" style={{ color: M.gold, fill: M.gold }} />
                  {s.processCursive.lines.map((l, i) => (
                    <div key={i} className="serif italic text-[12px]" style={{ color: M.tealMid }}>{l}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Chain (slide 5) */}
        {s.chain && (
          <div className="mt-4 rounded-2xl border p-4" style={{ background: M.white, borderColor: `${M.tealMid}33` }}>
            {s.chainTitle && (
              <div className="text-center text-[11px] tracking-[0.22em] font-bold uppercase mb-3" style={{ color: M.tealMid }}>
                {s.chainTitle}
              </div>
            )}
            <div className="grid grid-cols-4 gap-1 items-center">
              {s.chain.map((c, i) => (
                <React.Fragment key={i}>
                  <div className="text-center">
                    <div className="mx-auto w-11 h-11 rounded-full flex items-center justify-center mb-1.5" style={{ background: M.tealMid }}>
                      {I(c.icon, { size: 18, style: { color: M.white } })}
                    </div>
                    <div className="font-bold text-[10px] tracking-wide leading-snug" style={{ color: M.tealMid }}>{c.h}</div>
                    <div className="font-bold text-[10px] tracking-wide leading-snug" style={{ color: M.tealMid }}>{c.h2}</div>
                  </div>
                  {i < s.chain.length - 1 && (
                    <div className="text-center text-2xl" style={{ color: M.gold, position: "absolute", marginLeft: "-12px" }}>›</div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* RIGHT */}
      <div className="space-y-3">
        {s.rightPanels?.map((p, i) => (
          <div key={i} className="rounded-2xl p-4 border" style={{ background: M.white, borderColor: `${M.tealMid}33` }}>
            <div className="text-[11px] tracking-[0.22em] font-bold uppercase mb-3 leading-snug" style={{ color: M.ink }}>
              {p.title}
            </div>
            {p.iconedItems && (
              <div className="space-y-1.5">
                {p.iconedItems.map((it, j) => (
                  <div key={j} className="flex items-start gap-2 text-sm" style={{ color: M.ink }}>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: M.tealMid }}>
                      {I(it.icon, { size: 12, style: { color: M.white } })}
                    </div>
                    <span className="leading-snug pt-0.5">{it.text}</span>
                  </div>
                ))}
              </div>
            )}
            {p.numberedItems && (
              <div className="space-y-2">
                {p.numberedItems.map((it, j) => {
                  const col = colorFor(it.color);
                  return (
                    <div key={j} className="flex items-start gap-2">
                      <div className="relative shrink-0">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: col }}>
                          {I(it.icon, { size: 14, style: { color: M.white } })}
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border-2" style={{ background: M.white, color: col, borderColor: M.white }}>
                          {it.n}
                        </div>
                      </div>
                      <div className="flex-1 pt-0.5">
                        <div className="font-bold text-[11px] tracking-wide" style={{ color: col }}>{it.h}</div>
                        <div className="text-[10.5px] leading-snug" style={{ color: M.inkSoft }}>{it.t}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {p.goldDots && (
              <div className="space-y-1.5">
                {p.goldDots.map((it, j) => (
                  <div key={j} className="flex items-center gap-2 text-sm" style={{ color: M.inkSoft }}>
                    <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: j === p.goldDots.length - 1 ? M.green : M.gold }}>
                      <Heart size={9} style={{ color: M.white, fill: M.white }} />
                    </div>
                    <span>{it}</span>
                  </div>
                ))}
              </div>
            )}
            {p.emphasisHeadline && (
              <div className="mt-3 pt-3 text-center font-bold tracking-wide text-sm" style={{ color: M.gold, borderTop: `1px solid ${M.gold}33` }}>
                {p.emphasisHeadline}
              </div>
            )}
          </div>
        ))}

        {/* Quote box */}
        {s.quoteBox && (
          <div className="rounded-2xl p-4 relative" style={{ background: M.teal, color: M.cream }}>
            <Quote size={14} className="absolute top-2 left-2 opacity-50" style={{ color: M.gold }} />
            {s.quoteBox.cursiveTop && (
              <div className="serif italic text-base mb-1 pl-4" style={{ color: M.goldSoft }}>{s.quoteBox.cursiveTop}</div>
            )}
            <p className="text-sm leading-snug pl-4" style={{ color: M.cream }}>{s.quoteBox.text}</p>
            {s.quoteBox.cursive && (
              <div className="serif italic mt-2 text-center" style={{ color: M.goldSoft, fontSize: "clamp(0.95rem,1.2vw,1.15rem)" }}>
                {s.quoteBox.cursive}
              </div>
            )}
            <div className="mt-2 flex justify-center">
              <Heart size={12} style={{ color: M.gold, fill: M.gold }} />
            </div>
          </div>
        )}

        {/* Belief box (slide 5) */}
        {s.beliefBox && (
          <div className="rounded-2xl p-4 text-center" style={{ background: M.teal, color: M.cream }}>
            <Heart size={16} className="mx-auto mb-2" style={{ color: M.gold, fill: M.gold }} />
            {s.beliefBox.lines.map((l, i) => (
              <div key={i} className="font-bold text-xs tracking-wide" style={{ color: M.cream }}>{l}</div>
            ))}
            {s.beliefBox.cursive && (
              <div className="serif italic mt-2" style={{ color: M.goldSoft, fontSize: "1rem" }}>{s.beliefBox.cursive}</div>
            )}
            <div className="mt-2 flex justify-center">
              <Heart size={12} style={{ color: M.gold, fill: M.gold }} />
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Bottom Banner */}
    {s.bottomBanner && (
      <div className="mt-4 rounded-2xl px-5 py-3 relative overflow-hidden" style={{
        background: `linear-gradient(90deg, ${M.tealDeep}, ${M.teal}, ${M.greenDeep})`,
        color: M.cream,
      }}>
        <div className="flex items-center gap-4">
          <InfinityHeart size={32} />
          <div className="flex-1 text-center">
            {s.bottomBanner.script && (
              <div className="serif italic" style={{ color: M.goldSoft, fontSize: "clamp(0.95rem,1.3vw,1.25rem)" }}>
                {s.bottomBanner.script}
              </div>
            )}
            <div className="text-sm md:text-base font-bold tracking-wide" style={{ color: M.cream }}>
              {s.bottomBanner.lineA}
            </div>
            {s.bottomBanner.sub && (
              <div className="mt-1 text-[10px] tracking-[0.28em] uppercase font-semibold" style={{ color: M.gold }}>
                {s.bottomBanner.sub}
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Herzbotschafter CTA — only on final slide (9) */}
    {s.num === 9 && (
      <div className="mt-3 flex items-center justify-center">
        <Link
          to="/herzbewegung/herzbotschafter"
          className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full text-sm font-semibold transition hover:translate-y-[-2px] shadow-lg"
          style={{
            background: `linear-gradient(90deg, ${M.gold}, ${M.tealMid})`,
            color: M.tealDeep,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            boxShadow: `0 8px 24px ${M.gold}55`,
          }}
          data-testid="herzbewegung-cta-become-ambassador"
        >
          <Heart size={16} fill={M.tealDeep} />
          Jetzt Herzbotschafter werden
        </Link>
      </div>
    )}
  </div>
);

const render = (s) => (s.type === "cover" ? <Cover s={s} /> : <Content s={s} />);

export default function SatHerzbewegungDeck() {
  const [idx, setIdx] = useState(0);
  const [fs, setFs] = useState(false);
  const total = slides.length;
  const go = useCallback((d) => setIdx((i) => Math.min(total - 1, Math.max(0, i + d))), [total]);

  useEffect(() => {
    const k = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "f") setFs((f) => !f);
      else if (e.key === "Escape") setFs(false);
      else if (e.key === "Home") setIdx(0);
      else if (e.key === "End") setIdx(total - 1);
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [go, total]);

  const slide = slides[idx];

  return (
    <div
      className={`${fs ? "fixed inset-0 z-[100]" : "min-h-screen pt-24 pb-12"}`}
      style={{ background: M.tealDeep }}
      data-testid="sat-herzbewegung-deck"
    >
      <div className={`${fs ? "h-screen" : ""} container-sat`}>
        {!fs && (
          <div className="flex items-center justify-between mb-6">
            <Link
              to="/akademie/decks"
              className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase transition hover:translate-x-[-2px]"
              style={{ color: M.gold }}
              data-testid="herzbewegung-deck-back"
            >
              <ArrowLeft size={12} /> Zurück
            </Link>
            <div className="flex items-center gap-3">
              <div className="text-[10px] tracking-[0.35em] uppercase" style={{ color: "rgba(245,235,224,0.5)" }}>
                HERZBEWEGUNG · Initiative der SAT Akademie
              </div>
              <LiveActsPill />
            </div>
            <button
              onClick={() => setFs(true)}
              className="flex items-center gap-2 text-xs uppercase tracking-wider"
              style={{ color: M.gold }}
              data-testid="herzbewegung-deck-fullscreen-on"
            >
              <Maximize2 size={14} /> Vollbild
            </button>
          </div>
        )}

        <div
          className={`relative ${fs ? "h-full" : "min-h-[700px]"} rounded-2xl overflow-hidden`}
          style={{
            background: `linear-gradient(135deg, ${M.cream} 0%, ${M.creamSoft} 60%, ${M.cream} 100%)`,
            border: fs ? "none" : `1px solid ${M.gold}55`,
          }}
          data-testid={`herzbewegung-slide-${slide.num}`}
        >
          {/* LEFT brand strip (dark teal) */}
          <div
            className="absolute top-0 bottom-0 left-0 w-[80px] lg:w-[120px] flex flex-col items-center py-7 z-10"
            style={{ background: M.teal, color: M.cream }}
          >
            <InfinityHeart size={56} />
            <div className="mt-3 text-center leading-tight">
              <div className="serif text-lg font-light tracking-wider">SAT</div>
              <div className="text-[7px] tracking-[0.25em] uppercase mt-1 px-2 opacity-80">
                Schweizer Akademie
              </div>
              <div className="text-[7px] tracking-[0.25em] uppercase opacity-80">
                für Transformation
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1">
              <span className="text-[8px]">🇨🇭</span>
              <Heart size={10} style={{ color: M.gold, fill: M.gold }} />
            </div>

            <div className="hidden lg:block mt-4 text-center px-2" style={{ fontFamily: "Georgia, serif", color: M.goldSoft, fontStyle: "italic" }}>
              <div className="text-sm">Nenad</div>
              <div className="text-sm">Vladović</div>
              <div className="text-[8px] tracking-[0.18em] uppercase mt-2 opacity-80" style={{ color: M.cream }}>
                Gründer der SAT Akademie
              </div>
              <div className="text-[8px] tracking-[0.18em] uppercase opacity-80" style={{ color: M.cream }}>
                für Transformation
              </div>
            </div>

            <div className="mt-auto pb-2 text-center text-[8px] tracking-[0.22em] uppercase leading-relaxed hidden lg:block px-1">
              <div style={{ color: M.gold }}>Deine Stimme.</div>
              <div className="opacity-65" style={{ color: M.cream }}>Deine Selbstverantwortung.</div>
              <div style={{ color: M.tealSoft }}>Deine Kraft.</div>
              <div style={{ color: M.goldSoft }}>Deine Integrität.</div>
              <div style={{ color: M.green }}>Dein Erbe.</div>
            </div>

            <div className="mt-2 mb-1">
              <div className="w-9 h-9 rounded-md flex items-center justify-center border" style={{ borderColor: M.gold, color: M.gold }}>
                <span className="text-sm font-light">{slide.num}</span>
              </div>
            </div>
          </div>

          {/* Page counter */}
          <div className="absolute top-5 right-6 text-xs tracking-[0.3em] uppercase font-light z-10" style={{ color: M.ink }}>
            {String(slide.num).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </div>

          <div className="pl-[80px] lg:pl-[120px]">
            <div>{render(slide)}</div>
          </div>

          {/* Footer */}
          <div className="absolute bottom-0 left-[80px] lg:left-[120px] right-0 text-center text-[9px] tracking-[0.3em] uppercase py-2" style={{ background: M.teal, color: M.cream }}>
            HERZBEWEGUNG™ — Eine Initiative der SAT Akademie für Transformation 🇨🇭
          </div>

          {fs && (
            <button
              onClick={() => setFs(false)}
              className="absolute top-5 right-1/2 translate-x-1/2 flex items-center gap-2 text-xs uppercase tracking-wider z-20"
              style={{ color: M.ink }}
              data-testid="herzbewegung-deck-fullscreen-off"
            >
              <Minimize2 size={14} /> Schließen (Esc)
            </button>
          )}
        </div>

        {/* Controls */}
        <div className="mt-5 flex items-center justify-between">
          <button
            onClick={() => go(-1)}
            disabled={idx === 0}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm disabled:opacity-30"
            style={{ borderColor: `${M.gold}55`, color: M.gold }}
            data-testid="herzbewegung-deck-prev"
          >
            <ChevronLeft size={16} /> Vorherige
          </button>
          <div className="hidden md:flex items-center gap-1.5 max-w-2xl overflow-x-auto">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className="shrink-0 transition-all rounded-full"
                style={{
                  width: i === idx ? "24px" : "8px",
                  height: "8px",
                  background: i === idx ? M.gold : "rgba(212,175,55,0.3)",
                }}
                aria-label={`Slide ${i + 1}`}
                data-testid={`herzbewegung-deck-dot-${i}`}
              />
            ))}
          </div>
          <button
            onClick={() => go(1)}
            disabled={idx === total - 1}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-30"
            style={{ background: `linear-gradient(90deg, ${M.tealMid}, ${M.gold})`, color: M.cream }}
            data-testid="herzbewegung-deck-next"
          >
            Nächste <ChevronRight size={16} />
          </button>
        </div>

        {!fs && (
          <div className="mt-6 text-center text-[10px] tracking-[0.3em] uppercase" style={{ color: "rgba(245,235,224,0.4)" }}>
            Pfeiltasten ◄ ► · Leertaste · F für Vollbild · Esc zum Schließen
          </div>
        )}
      </div>
    </div>
  );
}
